import { createSlice } from "@reduxjs/toolkit";
import {
  fetchVitalSignals,
  addVitalSignal,
  updateVitalSignal,
  deleteVitalSignal,
} from "../../redux/thunks/vitalThunks";

// Helpers
const keyOf = (x = {}) =>
  x?.id ?? x?._id ?? x?.clientId ?? x?.uuid ??
  `${x?.type ?? ""}-${x?.value ?? ""}-${x?.measuredAt ?? x?.addedAt ?? x?.createdAt ?? ""}`;

const normalizeType = (type) => {
  const raw = String(type || "").trim();
  const key = raw.toUpperCase().replace(/[\s-]+/g, "_");

  if (["GLYCEMIA", "GLYCEMIE", "BLOOD_GLUCOSE", "BLOOD_SUGAR", "GLUCOSE"].includes(key)) {
    return "GLYCEMIA";
  }
  if (["HEART_RATE", "HEARTRATE", "PULSE", "CARDIAC_RATE"].includes(key)) {
    return "HEART_RATE";
  }
  if (["RESP_RATE", "RESPIRATORY_RATE", "RESPIRATION_RATE", "BREATHING_RATE"].includes(key)) {
    return "RESP_RATE";
  }
  if (["BP_SYS", "SYSTOLIC", "SYSTOLIC_PRESSURE", "BLOOD_PRESSURE_SYS"].includes(key)) {
    return "BP_SYS";
  }
  if (["BP_DIA", "DIASTOLIC", "DIASTOLIC_PRESSURE", "BLOOD_PRESSURE_DIA"].includes(key)) {
    return "BP_DIA";
  }
  if (["BLOOD_PRESSURE", "TENSION_ARTERIELLE"].includes(key)) {
    return "BLOOD_PRESSURE";
  }

  return key || raw;
};

const normalizeVitalSignal = (item) => {
  if (!item || typeof item !== "object") return item;

  const type = normalizeType(
    item.type ??
      item.signalType ??
      item.vitalType ??
      item.measurementType ??
      item.name ??
      item.code
  );

  return {
    ...item,
    id: item.id ?? item._id ?? item.uuid,
    type,
    value: item.value ?? item.reading ?? item.measurement ?? item.result,
    measuredAt:
      item.measuredAt ??
      item.measured_at ??
      item.recordedAt ??
      item.recorded_at ??
      item.createdAt ??
      item.date,
  };
};

// ✅ Used only for addVitalSignal (merge single new entry into existing list)
const mergeUniqueAndSort = (left = [], right = []) => {
  const map = new Map();
  [...left, ...right].forEach((item) => {
    const normalized = normalizeVitalSignal(item);
    if (normalized) map.set(keyOf(normalized), normalized);
  });
  const arr = [...map.values()];
  arr.sort(
    (a, b) =>
      new Date(b?.measuredAt ?? b?.addedAt ?? b?.createdAt ?? 0) -
      new Date(a?.measuredAt ?? a?.addedAt ?? a?.createdAt ?? 0)
  );
  return arr;
};

// ✅ Simple sort — used after full refetch (no merging, just sort fresh data)
const sortByDate = (arr = []) =>
  [...arr].sort(
    (a, b) =>
      new Date(b?.measuredAt ?? b?.addedAt ?? b?.createdAt ?? 0) -
      new Date(a?.measuredAt ?? a?.addedAt ?? a?.createdAt ?? 0)
  );

// ✅ Normalize payload — handles both raw array and { data: [...] } shapes
const unwrap = (payload) => {
  let value = payload;
  for (let i = 0; i < 4; i += 1) {
    if (!value || Array.isArray(value)) return value;
    if (Array.isArray(value.data) || value.data?.id != null || value.data?._id != null) {
      value = value.data;
      continue;
    }
    if (Array.isArray(value.vitals)) return value.vitals;
    if (Array.isArray(value.signals)) return value.signals;
    if (Array.isArray(value.items)) return value.items;
    if (Array.isArray(value.results)) return value.results;
    if (Array.isArray(value.records)) return value.records;
    return value;
  }
  return value;
};

const extractArray = (payload) => {
  const value = unwrap(payload);
  if (Array.isArray(value)) return value.map(normalizeVitalSignal).filter(Boolean);
  if (Array.isArray(value?.data)) return value.data.map(normalizeVitalSignal).filter(Boolean);
  if (Array.isArray(value?.vitals)) return value.vitals.map(normalizeVitalSignal).filter(Boolean);
  if (Array.isArray(value?.signals)) return value.signals.map(normalizeVitalSignal).filter(Boolean);
  if (Array.isArray(value?.items)) return value.items.map(normalizeVitalSignal).filter(Boolean);
  if (Array.isArray(value?.results)) return value.results.map(normalizeVitalSignal).filter(Boolean);
  if (Array.isArray(value?.records)) return value.records.map(normalizeVitalSignal).filter(Boolean);
  return [];
};

// ✅ Normalize single item — handles both raw object and { data: {...} } shapes
const extractItem = (payload) => {
  const value = unwrap(payload);
  if (!value) return null;
  if (value?.id != null || value?._id != null || value?.type || value?.signalType || value?.vitalType) {
    return normalizeVitalSignal(value);
  }
  return Array.isArray(value) ? normalizeVitalSignal(value[0]) ?? null : null;
};

// State
const initialState = { signals: [], loading: false, error: null };

const vitalSlice = createSlice({
  name: "vital-signs",
  initialState,
  reducers: {
    resetSignals:     () => ({ ...initialState }),
    clearError:       (state) => { state.error = null; },
    optimisticAdd:    (state, { payload }) => {
      state.signals = mergeUniqueAndSort([payload], state.signals);
    },
    removeByClientId: (state, { payload: clientId }) => {
      state.signals = state.signals.filter((s) => s.clientId !== clientId);
    },
  },
  extraReducers: (b) => {

    // ─── fetch ───────────────────────────────────────────────────
    b.addCase(fetchVitalSignals.pending, (s) => {
      s.loading = true;
      s.error = null;
    });
    b.addCase(fetchVitalSignals.fulfilled, (s, { payload }) => {
      s.loading = false;
      // ✅ FULL REPLACE — fresh fetch always wins, no stale data kept
      const incoming = extractArray(payload);
      s.signals = sortByDate(incoming);
    });
    b.addCase(fetchVitalSignals.rejected, (s, a) => {
      s.loading = false;
      s.error = a.payload ?? "Erreur lors du chargement des mesures.";
    });

    // ─── add ─────────────────────────────────────────────────────
    b.addCase(addVitalSignal.pending, (s) => {
      s.loading = true;
      s.error = null;
    });
    b.addCase(addVitalSignal.fulfilled, (s, { payload }) => {
      s.loading = false;
      // ✅ handles both { id, type, ... } and { data: { id, type, ... } }
      const serverItem = extractItem(payload);
      if (serverItem) s.signals = mergeUniqueAndSort([serverItem], s.signals);
    });
    b.addCase(addVitalSignal.rejected, (s, a) => {
      s.loading = false;
      s.error = a.payload?.message ?? a.payload ?? "Erreur lors de l'ajout.";
    });

    // ─── update ──────────────────────────────────────────────────
    b.addCase(updateVitalSignal.pending, (s) => {
      s.loading = true;
      s.error = null;
    });
    b.addCase(updateVitalSignal.fulfilled, (s, { payload }) => {
      s.loading = false;
      const updated = extractItem(payload);
      if (!updated) return;
      const i = s.signals.findIndex((x) => x?.id === updated.id);
      if (i !== -1) s.signals[i] = updated;
      else s.signals = mergeUniqueAndSort([updated], s.signals);
      s.signals = sortByDate(s.signals);
    });
    b.addCase(updateVitalSignal.rejected, (s, a) => {
      s.loading = false;
      s.error = a.payload ?? "Erreur lors de la mise à jour.";
    });

    // ─── delete ──────────────────────────────────────────────────
    b.addCase(deleteVitalSignal.pending, (s) => {
      s.loading = true;
      s.error = null;
    });
    b.addCase(deleteVitalSignal.fulfilled, (s, { payload }) => {
      s.loading = false;
      const id = payload?.id ?? extractItem(payload)?.id;
      if (!id) return;
      s.signals = s.signals.filter(
        (x) => x?.id !== id && x?._id !== id && x?.clientId !== id
      );
    });
    b.addCase(deleteVitalSignal.rejected, (s, a) => {
      s.loading = false;
      s.error = a.payload ?? "Erreur lors de la suppression.";
    });
  },
});

export const { resetSignals, clearError, optimisticAdd, removeByClientId } = vitalSlice.actions;
export default vitalSlice.reducer;
